/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import Autoplay from "embla-carousel-autoplay";
import React, { useEffect, useMemo, useState } from "react";
import useLocale from "~/hook/useLocale";
import { HeroBanner } from "~/interface/home/home";
import ImageWithSkeleton from "../shared-components/ImageWithSkeleton";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "../ui/carousel";
import { FALLBACK_IMAGE_ALT_TEXT } from "~/utils/constant";

interface HeroProps {
  images: HeroBanner[];
}

const Hero = ({ images }: HeroProps) => {
  const plugin = React.useRef(Autoplay({ delay: 2000, stopOnInteraction: false }));
  const { LocalizedLink } = useLocale();
  // Removed incorrect useMemo usage; if you need memoized values, use:
  const banner = useMemo(() => images, [images]);

  const [carouselApi, setCarouselApi] = useState<any>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [progress, setProgress] = React.useState(0);

  useEffect(() => {
    if (!carouselApi) return;
    let timeout: NodeJS.Timeout;

    const onSelect = () => {
      setProgress(0); // Reset progress
      setCurrentIndex(carouselApi.selectedScrollSnap());

      if (plugin.current && banner.length > 1) {
        plugin.current.stop();
        timeout = setTimeout(() => {
          plugin.current?.play();
        }, 1000); // Restart autoplay after 1 sec
      }
    };

    onSelect(); // Set initial index
    carouselApi.on("select", onSelect);

    return () => {
      carouselApi.off("select", onSelect);
      clearTimeout(timeout);
    };
  }, [carouselApi]);

  const handleDotClick = (index: number) => {
    setCurrentIndex(index);
    carouselApi?.scrollTo(index); // Programmatically scroll the carousel
    setProgress(0); // Reset progress on dot click
    if (plugin.current) {
      plugin.current.stop();
      setTimeout(() => {
        plugin.current?.play();
      }, 1000); // Restart autoplay after 1 sec
    }
  };

  return (
    <div className="w-full mx-auto  flex flex-col gap-between-dot-and-content">
      <Carousel plugins={[plugin.current]} className="w-full lg:px-3 overflow-hidden" setApi={setCarouselApi} opts={{ loop: true }}>
        <CarouselContent>
          {banner.map((data, index) => (
            <CarouselItem key={index} className="h-auto w-full cursor-pointer overflow-hidden flex justify-center items-center">
              <LocalizedLink to={data.navigate_to} target={data.new_tab ? "_blank" : "_self"}>
                <div className="aspect-[16/9] flex justify-center items-center xs:h-[190px] smd:h-[200px] sml:h-[230px] sml:mr-0 md:h-[280px] md:mr-0 lg:h-[400px] 2xl:h-[650px] hero-banner rounded-xl overflow-hidden">
                  <ImageWithSkeleton
                    // url={{
                    //   small: data.img.s.url,
                    //   medium: data.img.m.url,
                    //   large: data.img.l.url,
                    //   hd: data.img.hd.url,
                    //   original: data.img.og.url,
                    // }}
                    url={{
                      small: data.img.hd.url,
                      medium: data.img.hd.url,
                      large: data.img.hd.url,
                      hd: data.img.hd.url,
                      original: data.img.hd.url,
                    }}
                    alt={data.name || FALLBACK_IMAGE_ALT_TEXT}
                    blur_hash={data.img.bh}
                    className="aspect-[16/9] object-contain rounded-xl"
                    height={data.img.ar}
                    roundedClass="rounded-xl"
                    isPreloaded={true}
                  />
                </div>
              </LocalizedLink>
            </CarouselItem>
          ))}
        </CarouselContent>
        {banner.length > 1 ? (
          <>
            <CarouselNext className="absolute md:right-10 lg:right-24 2xl:right-20 hidden md:flex size-11 !bg-darkOrangeColor !text-white border-none hover:bg-hoverColor hover:text-textHeadingColor" />
            <CarouselPrevious className="absolute md:left-10 lg:left-24 2xl:left-20 hidden md:flex size-11 !bg-darkOrangeColor !text-white border-none hover:bg-hoverColor hover:text-textHeadingColor" />
          </>
        ) : null}
      </Carousel>
      <div className="mx-auto w-full flex justify-center ">
        {banner.map((_, index) => (
          <div key={index} className="flex justify-center items-center cursor-pointer" onClick={() => handleDotClick(index)}>
            <div
              key={index}
              className={`w-3 h-1 mx-1 rounded-full transition-all relative overflow-hidden ${
                currentIndex === index ? "w-9 bg-darkOrangeColor" : "w-3 bg-lightbackground"
              } `}
            ></div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Hero;
